import React, { useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import WelcomePage from './pages/WelcomePage/index';
import ProjectDetailsPage from './pages/ProjectDetailsPage/index';
import PortfolioPage from './pages/PortfolioPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import DisclaimerPage from './pages/DisclaimerPage';
import About from './pages/About';
import AffiliateProgram from './pages/AffiliateProgram';
import GoalAnalysisPage from './pages/GoalAnalysisPage';
import ProfilePage from './pages/ProfilePage';
import ScreenerPage from './pages/ScreenerPage';
import CompanySearchPage from './pages/CompanySearchPage';
import Header from './components/Header';
import Footer from './components/Footer';
import { useAuth } from './hooks/useAuth';
// Admin pages
import IndustryInsightsPage from './pages/admin/IndustryInsightsPage';
import NoteFormPage from './pages/admin/NoteFormPage';

const App: React.FC = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationAttemptedRef = useRef(false);

  console.log('App rendering. User:', user, 'Loading:', loading, 'Location:', location.pathname);

  // Handle navigation for protected routes
  useEffect(() => {
    console.log('App useEffect. User:', user, 'Loading:', loading, 'Location:', location.pathname);
    if (!loading && !user) {
      if (location.pathname === '/welcome' || 
          location.pathname === '/portfolio' || 
          location.pathname.startsWith('/projects/') ||
          location.pathname.startsWith('/dashboard/')) {
        console.log('Redirecting unauthenticated user to landing page');
        navigate('/', { replace: true });
      }
    }
  }, [user, navigate, location.pathname, loading]);

  // Loading component
  const LoadingScreen = () => (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      backgroundColor: 'var(--white)'
    }}>
      <div style={{
        textAlign: 'center',
        color: 'var(--primary-blue)',
        opacity: 0.8
      }}>
        <h2>Loading AskCEO.ai...</h2>
        <p style={{ marginTop: '1rem', fontSize: '0.9rem' }}>Please wait while we authenticate your session</p>
      </div>
    </div>
  );

  // Only show loading screen during initial load
  if (loading && !navigationAttemptedRef.current) {
    return <LoadingScreen />;
  }

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      minHeight: '100vh',
      backgroundColor: '#f0f0f0'
    }}>
      <Header />
      <main style={{ 
        flex: 1, 
        padding: window.innerWidth < 768 ? '1rem' : '2rem',
        maxWidth: '1400px',
        margin: '0 auto',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route
            path="/welcome" 
            element={
              user ? <WelcomePage /> : <Navigate to="/" replace />
            }
          />
          {/* Admin Routes */}
          <Route path="/admin/industry-insights" element={user ? <IndustryInsightsPage /> : <Navigate to="/" replace />} />
          <Route path="/admin/industry-insights/new" element={user ? <NoteFormPage /> : <Navigate to="/" replace />} />
          <Route path="/admin/industry-insights/edit/:industry/:category/:filename" element={user ? <NoteFormPage /> : <Navigate to="/" replace />} />
          <Route 
            path="/portfolio" 
            element={
              user ? <PortfolioPage /> : <Navigate to="/" replace />
            }
          />
          <Route 
            path="/portfolio/:id" 
            element={
              user ? <PortfolioPage /> : <Navigate to="/" replace />
            }
          />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/disclaimer" element={<DisclaimerPage />} />
          <Route path="/about" element={<About />} />
          <Route path="/affiliate-program" element={<AffiliateProgram />} />
          <Route
            path="/tools/goal-analysis/strategic-asset-allocation-saa"
            element={
              user ? <GoalAnalysisPage /> : <Navigate to="/" replace />
            }
          />
          <Route
            path="/profile"
            element={
              user ? <ProfilePage /> : <Navigate to="/" replace />
            }
          />
          <Route
            path="/screeners"
            element={
              user ? <ScreenerPage /> : <Navigate to="/" replace />
            }
          />
          <Route
            path="/company-search"
            element={
              user ? <CompanySearchPage /> : <Navigate to="/" replace />
            }
          />
          <Route
            path="/projects/:projectId"
            element={
              user ? <ProjectDetailsPage /> : <Navigate to="/" replace />
            }
          />
          <Route
            path="/dashboard/:projectId"
            element={
              user ? <ProjectDetailsPage /> : <Navigate to="/" replace />
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
};

export default App;
